import * as THREE from 'three';
import { LANE_WIDTH, TRACK } from '../game/tuning';
import { buildObstacleAssets, type ObstacleFamily, type ObstacleAssets } from '../assets/obstacleFactory';

export interface Obstacle {
  family: ObstacleFamily;
  /** Current world x (drones move between lanes). */
  x: number;
  z: number;
  active: boolean;
  /** Near-miss already awarded or collision consumed. */
  passed: boolean;
  halfWidth: number;
  halfLength: number;
  // Drone oscillation.
  oscillateFrom: number;
  oscillateTo: number;
  oscillatePhase: number;
  oscillateSpeed: number;
}

const MAX_PER_FAMILY = 48;
const dummy = new THREE.Object3D();

/** Per-family warning-light animation: each hazard breathes/blinks its own way. */
const SIGNAL_PULSE: Record<ObstacleFamily, (t: number) => number> = {
  firewall: (t) => 1.8 + Math.sin(t * 6) * 0.55,
  pylon: (t) => 1.9 + Math.sin(t * 2.3) * 0.35,
  drone: (t) => 2.0 + Math.sin(t * 9) * 0.6,
  gate: (t) => 1.7 + (Math.sin(t * 4) > 0 ? 0.7 : 0),
};

/**
 * All obstacles live in per-family InstancedMeshes; logic data in flat pools.
 * Collision is a simple AABB overlap in x/z around the player at z ~ 0.
 */
export class ObstacleSystem {
  readonly group = new THREE.Group();

  private readonly assets: ObstacleAssets;
  private readonly pools = new Map<ObstacleFamily, Obstacle[]>();

  constructor() {
    this.assets = buildObstacleAssets(MAX_PER_FAMILY);
    for (const [family, pair] of this.assets.meshes) {
      this.group.add(pair.body);
      this.group.add(pair.signal);
      const pool: Obstacle[] = [];
      for (let i = 0; i < MAX_PER_FAMILY; i += 1) {
        pool.push({
          family,
          x: 0,
          z: 0,
          active: false,
          passed: false,
          halfWidth: this.assets.footprints.get(family)!.halfWidth,
          halfLength: this.assets.footprints.get(family)!.halfLength,
          oscillateFrom: 0,
          oscillateTo: 0,
          oscillatePhase: 0,
          oscillateSpeed: 0,
        });
      }
      this.pools.set(family, pool);
    }
  }

  spawn(
    family: ObstacleFamily,
    lane: -1 | 0 | 1,
    z: number,
    drone?: { toLane: -1 | 0 | 1; phase: number; speed: number },
  ): boolean {
    const pool = this.pools.get(family);
    if (!pool) return false;
    const slot = pool.find((o) => !o.active);
    if (!slot) return false;
    slot.active = true;
    slot.passed = false;
    slot.x = lane * LANE_WIDTH;
    slot.z = z;
    slot.oscillateFrom = lane * LANE_WIDTH;
    slot.oscillateTo = (drone ? drone.toLane : lane) * LANE_WIDTH;
    slot.oscillatePhase = drone?.phase ?? 0;
    slot.oscillateSpeed = drone?.speed ?? 0;
    return true;
  }

  update(distanceDelta: number, elapsed: number): void {
    for (const [family, pool] of this.pools) {
      const pair = this.assets.meshes.get(family)!;
      (pair.signal.material as THREE.MeshStandardMaterial).emissiveIntensity =
        SIGNAL_PULSE[family](elapsed);
      let count = 0;
      for (const o of pool) {
        if (!o.active) continue;
        o.z += distanceDelta;
        if (o.z > TRACK.despawnZ) {
          o.active = false;
          continue;
        }
        if (o.oscillateSpeed > 0) {
          const t = 0.5 + 0.5 * Math.sin(elapsed * o.oscillateSpeed + o.oscillatePhase);
          o.x = o.oscillateFrom + (o.oscillateTo - o.oscillateFrom) * t;
        }
        dummy.position.set(o.x, 0, o.z);
        dummy.rotation.set(0, 0, 0);
        if (family === 'drone') {
          dummy.position.y = 0.75 + Math.sin(elapsed * 5 + o.oscillatePhase) * 0.12;
          dummy.rotation.y = elapsed * 2 + o.oscillatePhase;
        }
        dummy.updateMatrix();
        pair.body.setMatrixAt(count, dummy.matrix);
        pair.signal.setMatrixAt(count, dummy.matrix);
        count += 1;
      }
      pair.body.count = count;
      pair.signal.count = count;
      pair.body.instanceMatrix.needsUpdate = true;
      pair.signal.instanceMatrix.needsUpdate = true;
    }
  }

  /** Returns the first obstacle whose AABB overlaps the player footprint. */
  collide(playerX: number, playerHalfWidth: number, playerHalfLength: number): Obstacle | null {
    for (const pool of this.pools.values()) {
      for (const o of pool) {
        if (!o.active || o.passed) continue;
        if (Math.abs(o.z) > o.halfLength + playerHalfLength + 2) continue;
        const overlapX = Math.abs(o.x - playerX) < o.halfWidth + playerHalfWidth;
        const overlapZ = Math.abs(o.z) < o.halfLength + playerHalfLength;
        if (overlapX && overlapZ) return o;
      }
    }
    return null;
  }

  /**
   * Awards near-misses: obstacles that just passed the player in an adjacent
   * position without colliding. Returns how many were newly passed close by.
   */
  countNearMisses(playerX: number, window: number): number {
    let count = 0;
    for (const pool of this.pools.values()) {
      for (const o of pool) {
        if (!o.active || o.passed) continue;
        if (o.z > o.halfLength + 0.6) {
          o.passed = true;
          const gap = Math.abs(o.x - playerX) - o.halfWidth;
          if (gap < window) count += 1;
        }
      }
    }
    return count;
  }

  activeCount(): number {
    let count = 0;
    for (const pool of this.pools.values()) {
      for (const o of pool) if (o.active) count += 1;
    }
    return count;
  }

  reset(): void {
    for (const pool of this.pools.values()) {
      for (const o of pool) o.active = false;
    }
    for (const pair of this.assets.meshes.values()) {
      for (const mesh of [pair.body, pair.signal]) {
        mesh.count = 0;
        mesh.instanceMatrix.needsUpdate = true;
      }
    }
  }

  dispose(): void {
    this.assets.dispose();
  }
}
